import { type TableFeature, type RowData, type Table, type Cell, type Column, type Row } from "@tanstack/react-table";

export type CellEditionType = "editing" | "viewing" | "adding" | "unset";
export type CellEditionState = Map<string, Map<string, CellEditionType>>;

export interface CellEditionTableState {
  cellEdition: CellEditionState;
}

export interface CellEditionTableInitialState {
  cellEdition?: CellEditionState;
}

export interface CellEditionInstance {
  _updateCellEditionState?: (state: CellEditionType) => void;
  setCellEditing?: () => void;
  setCellAdding?: () => void;
  setCellViewing?: () => void;
  getCellEditionState?: () => CellEditionType;
}

export type CellEditionSetupType = {
  rowId: string;
  columnId: string;
  state: CellEditionType;
};

export const CellEditionFeature: TableFeature = {
  getInitialState: (state): CellEditionTableState => {
    return {
      cellEdition: state?.cellEdition ?? new Map(),
      ...state,
    };
  },

  createCell: <TData extends RowData>(
    cell: Cell<TData, unknown>,
    column: Column<TData>,
    row: Row<TData>,
    table: Table<TData>,
  ) => {
    cell.getCellEditionState = () => {
      const rowState = table.getState().cellEdition.get(row.id);
      return rowState?.get(column.id) ?? "unset";
    };
    cell._updateCellEditionState = (state: CellEditionType) =>
      table.setState(old => {
        if (old.cellEdition.get(row.id)?.get(column.id) === state) {
          return old;
        }
        return {
          ...old,
          cellEdition: new Map(old.cellEdition).set(
            row.id,
            new Map(old.cellEdition.get(row.id) ?? new Map()).set(column.id, state),
          ),
        };
      });
    cell.setCellAdding = () => cell._updateCellEditionState?.("adding");
    cell.setCellEditing = () => cell._updateCellEditionState?.("editing");
    cell.setCellViewing = () => cell._updateCellEditionState?.("viewing");
  },
};
